// Reads the sales an affiliate campaign earned and normalises them into one row shape.
//
// The three sale types are stored three different ways, in three different units, with the
// purchase moment under three different keys. Everything downstream - KPI cards, Plotly
// charts, the DataGrid, the CSV - works on the normalised row, so this file is the only
// place that has to know any of that.
//
//   Reservation   customers/{cid}/reservations/{id}.affiliate           queryable
//   Event ticket  customers/{cid}/eventtickets/{id}.purchases[n]        embedded array
//   Participation customers/{cid}/activities/{id}.participants[n]       embedded array
//
// There are no attribution documents: these reads hit the live sale documents every time,
// which is what makes cancellations and refunds self-correcting.

import moment from 'moment'
import Reservation from '../Classes/Reservation'
import { isCancelledPurchase } from '../../eventtickets'
import { netFromGross, round2 } from './affiliateCommission'

// The key the enduser repo stamps the affiliate code onto. Kept as one constant because
// the spelling is owned by another repository - if it turns out to be 'afficiliate', this
// line and the Firestore query in firebase.getReservationsByAffiliate are the only edits.
export const AFFILIATE_FIELD = 'affiliate'

// Codes are compared case-insensitively: a code typed into a campaign and a code arriving
// off a URL should match regardless of how either was capitalised.
export const normaliseCode = (code) => (code === null || code === undefined ? '' : String(code).trim().toLowerCase())

export const campaignCodes = (campaign) =>
  ((campaign && campaign.codes) || [])
    .map((entry) => normaliseCode(entry && entry.code))
    .filter((code) => code.length > 0)

const codeOf = (doc) => normaliseCode(doc ? doc[AFFILIATE_FIELD] : '')

const matchesCode = (doc, codeSet) => {
  const code = codeOf(doc)
  return code.length > 0 && codeSet.has(code) ? code : null
}

// Purchase timestamps arrive in whatever format their writer used: the event ticket
// 'YYYY-MM-DD HH:mm:ssZ', the reservation viadate, an activity enrolment stamp, or a
// Firestore Timestamp. Anything unparseable becomes '' and drops out of the timeline
// rather than landing on the Unix epoch.
const KNOWN_FORMATS = [
  moment.ISO_8601,
  'YYYY-MM-DD HH:mm:ssZ',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD',
  'YYYY.MM.DD HH:mm',
  'DD.MM.YYYY HH:mm',
  'DD.MM.YYYY',
]

export const toDateKey = (value) => {
  if (!value) return ''
  if (typeof value === 'object') {
    // Firestore Timestamp, or something already a Date.
    if (typeof value.toDate === 'function') return moment(value.toDate()).format('YYYY-MM-DD')
    if (typeof value.seconds === 'number') return moment.unix(value.seconds).format('YYYY-MM-DD')
    if (value instanceof Date) return moment(value).format('YYYY-MM-DD')
    return ''
  }
  if (typeof value === 'number') return moment.unix(value > 1e11 ? value / 1000 : value).format('YYYY-MM-DD')
  const parsed = moment(String(value), KNOWN_FORMATS, true)
  if (parsed.isValid()) return parsed.format('YYYY-MM-DD')
  // Second pass non-strict against the SAME list, not a free-form moment(string): the
  // free-form parse logs a moment deprecation warning for every value it cannot read, and
  // an unparseable timestamp is not rare enough to spam the console over.
  const loose = moment(String(value), KNOWN_FORMATS)
  return loose.isValid() ? loose.format('YYYY-MM-DD') : ''
}

const personName = (person) => {
  if (!person) return ''
  const name = [person.firstname, person.lastname].filter(Boolean).join(' ').trim()
  return name || person.name || person.email || ''
}

const num = (value) => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value === null || value === undefined ? '' : value).replace(',', '.'))
  return isFinite(parsed) ? parsed : 0
}

const localised = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  return value.fi || value.en || value.sv || ''
}

/* Reservations */

// 'moved' counts as cancelled here the same way 'cancelled' does: the booking that earned
// the commission no longer exists, its replacement is a different document.
const CANCELLED_RESERVATION_STATES = ['cancelled', 'moved', 'deleted']

export const reservationRows = (reservations, codeSet) =>
  (reservations || [])
    .map((doc) => {
      const code = matchesCode(doc, codeSet)
      if (!code) return null

      // Reservation encapsulates cost/VAT/creation, and that logic is already duplicated
      // once in PersonDetails2 - do not make it three.
      //
      // Its getters reach into resourcegroups[0].resources[0] and payers[0].contacts[0]
      // without guarding, so each group is read in its own try/catch: a booking with a
      // missing resource or an organisation payer carrying no contacts must lose that ONE
      // field, not drop out of the report. Dropping a real sale is the worse failure.
      const reservation = new Reservation({ ...doc })
      let gross = 0
      let vat = 0
      let sourcename = doc.displayName || doc.title || ''
      let buyer = ''
      let date = ''

      try {
        gross = num(reservation.getCost())
        vat = num(reservation.getResourceVat())
        const resource = reservation.getResource()
        if (resource) sourcename = resource.displayName || resource.name || sourcename
      } catch (error) {
        console.error('Affiliate: reservation ' + doc.id + ' has no readable cost/resource', error)
      }

      try {
        buyer = personName(reservation.getFirstPayerSimple())
      } catch (error) {
        console.error('Affiliate: reservation ' + doc.id + ' has no readable payer', error)
      }

      try {
        // viadate is when the booking was MADE, which is the moment the affiliate link was
        // followed. resourcegroups[0].start is when the court is booked, which is not the
        // same thing and would put the sale in the wrong period.
        const creation = reservation.getCreation()
        date = toDateKey(creation && creation.date)
      } catch (error) {
        console.error('Affiliate: reservation ' + doc.id + ' has no readable creation date', error)
      }

      return {
        type: 'reservations',
        id: 'reservations-' + doc.id,
        sourceid: doc.id,
        sourcename,
        date,
        buyer,
        code,
        // A recurring booking counts as one sale at its total cost: the affiliate link was
        // followed once. Rare in practice - affiliate links are consumer flows and
        // recurring bookings are contract flows.
        quantity: 1,
        gross: round2(gross),
        net: netFromGross(gross, vat),
        vat,
        cancelled: CANCELLED_RESERVATION_STATES.includes(doc.state),
      }
    })
    .filter(Boolean)

/* Event tickets */

// One row per purchase line. Lines carry a quantity above one, and an order (one
// paymenttoken) can hold several lines - both stay visible, because a per-unit commission
// rule needs the quantity and a per-sale rule needs the line.
export const eventTicketRows = (eventtickets, codeSet) => {
  const rows = []
  ;(eventtickets || []).forEach((ticket) => {
    const purchases = (ticket && ticket.purchases) || []
    purchases.forEach((purchase, index) => {
      const code = matchesCode(purchase, codeSet)
      if (!code) return
      const quantity = Math.max(1, Math.round(num(purchase.quantity)) || 1)
      // purchase.price is EUR per ticket, purchase.amount is CENTS for the whole line.
      // Prefer price * quantity, which is what EventTicketsContainer reports as revenue.
      const gross = purchase.price !== undefined && purchase.price !== null
        ? num(purchase.price) * quantity
        : num(purchase.amount) / 100
      const vat = num(ticket.vat)
      rows.push({
        type: 'eventtickets',
        id: 'eventtickets-' + ticket.id + '-' + index,
        sourceid: ticket.id,
        sourcename: localised(ticket.name),
        date: toDateKey(purchase.timestamp),
        buyer: personName(purchase) || personName(purchase.user),
        code,
        quantity,
        gross: round2(gross),
        net: netFromGross(gross, vat),
        vat,
        // Cancelling is a soft state on the purchase, kept so the money history survives.
        // Same test activePurchases() applies, which is what the ticket views count with.
        cancelled: isCancelledPurchase(purchase),
      })
    })
  })
  return rows
}

/* Activity participations */

const CANCELLED_ACTIVITY_STATES = ['cancelled', 'deleted']

// A withdrawn participant is removed from participants[] by
// activities-removeParticipants, so they vanish from this list entirely rather than
// turning up cancelled. Only the activity itself being cancelled shows up as a
// cancelled row.
export const activityRows = (activities, codeSet) => {
  const rows = []
  ;(activities || []).forEach((activity) => {
    const participants = (activity && activity.participants) || []
    participants.forEach((participant, index) => {
      const code = matchesCode(participant, codeSet)
      if (!code) return
      // A member price (membershipPrices row or entry benefit) is resolved at enrolment
      // and stored on the participant in cents; without it the activity price was paid.
      const pricing = participant.pricing
      const gross = pricing && pricing.price !== undefined && pricing.price !== null ? num(pricing.price) / 100 : num(activity.price)
      const vat = num(activity.vat)
      rows.push({
        type: 'activities',
        id: 'activities-' + activity.id + '-' + (participant.id || index),
        sourceid: activity.id,
        sourcename: localised(activity.name),
        date: toDateKey(participant.entry),
        buyer: personName(participant),
        code,
        quantity: 1,
        gross: round2(gross),
        net: netFromGross(gross, vat),
        vat,
        cancelled: CANCELLED_ACTIVITY_STATES.includes(activity.state),
      })
    })
  })
  return rows
}

/* Fetch */

// Every distinct code across a set of campaigns, so the reservation queries are issued
// once per code rather than once per campaign per code.
export const allCampaignCodes = (campaigns) => {
  const seen = new Set()
  ;(campaigns || []).forEach((campaign) => campaignCodes(campaign).forEach((code) => seen.add(code)))
  return [...seen]
}

// The raw documents behind a set of codes, fetched once. Split out from row building so
// the campaign list can price every campaign off ONE pass: the event ticket and activity
// reads are whole-collection fetches, and doing them per campaign would be wasteful.
export const fetchSaleSources = async (firebase, codes) => {
  const list = (codes || []).filter(Boolean)
  if (list.length === 0) return { reservations: [], eventtickets: [], activities: [], errors: [] }

  const errors = []
  const guard = (label, promise) =>
    promise.catch((error) => {
      console.error('Affiliate: ' + label + ' failed', error)
      errors.push(label)
      return null
    })

  const [reservations, eventtickets, activitysnap] = await Promise.all([
    guard('varaukset', firebase.getReservationsByAffiliate(list)),
    guard('tapahtumaliput', firebase.getEventTickets()),
    guard('aktiviteetit', firebase.getActivities()),
  ])

  // getEventTickets() hands back mapped objects, getActivities() hands back the raw
  // snapshot and leaves the mapping to its caller. Absorb the inconsistency here.
  const activities =
    activitysnap && activitysnap.docs ? activitysnap.docs.map((doc) => ({ ...doc.data(), id: doc.id })) : []

  return { reservations: reservations || [], eventtickets: eventtickets || [], activities, errors }
}

// The normalised rows one campaign earned, out of already-fetched sources.
export const rowsForCampaign = (sources, campaign) => {
  const codes = campaignCodes(campaign)
  if (codes.length === 0 || !sources) return []
  const codeSet = new Set(codes)
  const rows = [
    ...reservationRows(sources.reservations, codeSet),
    ...eventTicketRows(sources.eventtickets, codeSet),
    ...activityRows(sources.activities, codeSet),
  ]
  // Newest purchase first, matching every other list in the app.
  rows.sort((a, b) => String(b.date).localeCompare(String(a.date)))
  return rows
}

// One campaign's sales, fetched and normalised. Thin wrapper for the report view, which
// only ever looks at a single campaign.
export const fetchAffiliateSales = async (firebase, campaign) => {
  const codes = campaignCodes(campaign)
  if (codes.length === 0) return { rows: [], errors: [] }
  const sources = await fetchSaleSources(firebase, codes)
  return { rows: rowsForCampaign(sources, campaign), errors: sources.errors }
}

// Rows whose PURCHASE date falls inside the range, ends included. An empty bound means
// unbounded; a row with no readable purchase date is kept, because dropping a real sale is
// worse than showing one outside the window.
export const filterByDateRange = (rows, start, end) =>
  (rows || []).filter((row) => {
    if (!row.date) return true
    if (start && row.date < start) return false
    if (end && row.date > end) return false
    return true
  })
